import type { FetchError } from 'ofetch';
/**
 * Removes properties with `undefined` values from an object.
 * This is useful for cleaning up objects before sending them in requests or using them where `undefined` values are not desirable.
 *
 * @param obj - The object to clean.
 * @returns A new object with all properties set to `undefined` removed.
 *
 * @example
 * const myObject = {
 *   name: 'John',
 *   age: undefined,
 *   city: 'New York',
 * };
 *
 * const cleanedObject = cleanUndefinedProperties(myObject);
 * // cleanedObject is now { name: 'John', city: 'New York' }
 */
export function cleanUndefinedProperties<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== undefined)) as Partial<T>;
}

/**
 * Creates a map from an array of items, grouped by a specified key.
 * The keyExtractor function defines how to extract the grouping key from each item.
 * @param items - The array of items to group into a map.
 * @param keyExtractor - A function that takes an item and returns a key for grouping.
 * @returns A map where each key is the result of keyExtractor, and each value is an array of items that share that key.
 */
export function createGroupedMap<T, K>(items: T[], keyExtractor: (item: T) => K): Map<K, T[]> {
  return items.reduce((acc: Map<K, T[]>, item: T) => {
    const key = keyExtractor(item);
    if (!acc.has(key)) {
      acc.set(key, []);
    }
    acc.get(key)?.push(item);
    return acc;
  }, new Map<K, T[]>());
}

export function checkIsCrawler(userAgent: string) {
  return /bot|crawl|spider|google|bing|yahoo|yandex|baidu|duckduckgo|facebookexternalhit|twitterbot|linkedinbot|slackbot|telegrambot|applebot|pingdom|tumblr|slurp|archiver|archive|curl|python|java|wget|ruby|scrapy|nutch|selenium|phantomjs|headlesschrome|pinterest|instagram|whatsapp|skypeuripreview|outbrain|adidxbot|alexabot|findlinks|msnbot|mj12bot|ahrefsbot|semrushbot|dotbot|exabot|gigabot|qwantify|sogou|exalead|ia_archiver|ltx71|mail\.ru|viber|wordpress|panscient|applebot|twitterbot|discordbot|slackbot|telegrambot|pinterestbot|showyoubot|addthis|feedfetcher|kaspersky|bitlybot|flipboard|tencent|baiduspider|blexbot|zoominfo|mp3bot|grabber|sistrix|serpstatbot|finbot|brandsmonitor|serpstatbot|lipperhey|ccbot|admantx|ahoy|dataminr|ubermetrics|spyonweb|ranksonic|acoonbot|linkdex|startmebot|deusu|privacyawarebot|genieo|msrbot|niki-bot|cloudflare|archive\.org_bot|megaindex|semrush|petalbot|aspiegelbot|avira|safedns|safebrowsing|virustotal|sucuri|unshorten|urlresolver|validator|voyager|w3c_validator|yacy|yandeximages|yeti|yisouspider|zumbot|zyborg|applebot|googlebot-image|googlebot-news|googlebot-video|adsbot-google|api[\w-]+|[\w-]*[sS]craper/i.test(
    userAgent ?? '',
  );
}

export function isFetchError(err: unknown): err is FetchError {
  try {
    return Boolean(err) && (err as FetchError)?.name === 'FetchError';
  } catch {
    return false;
  }
}
